import { useCallback } from 'react';
import { EntityId } from '@reduxjs/toolkit';
import { ClientType } from '@common/model/client/clientType';
import { useCurrentCity } from '@cp/entities/location';
import { ROUTES_DEFAULT_CITY_ID } from '@common/const/routes/defaultCity';
import { useIsLoggedIn } from '@cp/entities/currentUser';
import { Result } from '@cp/shared/result';
import { SPEvents, SPProvide, useSubscriptionProtection } from 'src/entities/subscription';
import { ICatalogItemState } from 'src/entities/catalog';
import { useCatalogItemViewService } from './useCatalogItemViewService';
import { CatalogItemType } from '@common/model/catalog/item/type';

const NUMBER_TO_ACCESS_OF_ITEMS = 2;

interface CIPParams {
  provide?: SPProvide;
  numberToAccessOfItems?: number;
  events?: SPEvents;
}

export const useCatalogItemProtection = (params?: CIPParams) => {
  const isLoggedIn = useIsLoggedIn();
  const catalogItemService = useCatalogItemViewService();
  const { currentCity } = useCurrentCity({ allowEmptyValue: true });
  const subscribe = useSubscriptionProtection({
    events: params?.events,
    provide: params?.provide,
  });
  return useCallback(
    (catalogItem: ICatalogItemState) => {
      let hasProtectionResult: Result<unknown, Error> = Result.Ok();
      const isResume = catalogItem.catalogType === CatalogItemType.Resume;
      const numberToAccessOfItems = params?.numberToAccessOfItems || NUMBER_TO_ACCESS_OF_ITEMS;
      const accessOfItems = catalogItemService.getAccessOfItems<EntityId[]>(catalogItem.catalogType);
      const hasToLargeAccessOfItems = accessOfItems.length >= numberToAccessOfItems && !accessOfItems.includes(catalogItem.id);

      if (hasToLargeAccessOfItems) {
        hasProtectionResult = subscribe({
          careerId: catalogItem.careerId,
          catalogType: catalogItem.catalogType,
          cityId: currentCity?.id || ROUTES_DEFAULT_CITY_ID,
          clientType: isResume ? ClientType.Employer : ClientType.Specialist,
        });
      }

      if (!isLoggedIn && hasProtectionResult.isOk) {
        catalogItemService.addAccessOfItem(catalogItem.id, catalogItem.catalogType);
      }

      return hasProtectionResult;
    },
    [catalogItemService, currentCity?.id, isLoggedIn, params?.numberToAccessOfItems, subscribe],
  );
};
